import { useEffect } from "react";

/**
 * Inject a per-route Schema.org JSON-LD block into <head>.
 *
 * Pass a serializable object (or array of objects) — the hook handles
 * stringification, dedup-by-id, and cleanup on unmount. Each invocation
 * tags its <script> with `data-jsonld-id` so the cleanup only removes
 * what this hook created (the static index.html LD block is untouched).
 *
 * Usage:
 *   useJsonLd("/faq", { "@context": "https://schema.org", "@type": "FAQPage", ... });
 *
 * Pass `null` as the data argument to deliberately skip injection (e.g.
 * while data is still loading).
 */
export function useJsonLd(id, data) {
  // We compare by serialized payload so identical-shape objects don't churn
  // the head on every render. Stringify outside the effect deps to satisfy
  // react-hooks/exhaustive-deps without inlining the call there.
  const serialized = data ? JSON.stringify(data) : null;
  useEffect(() => {
    if (!serialized || !id) return undefined;
    const key = `route:${id}`;
    // Remove any stale node with the same id first (route swap edge case).
    document.head.querySelectorAll(`script[data-jsonld-id="${key}"]`).forEach((n) => n.remove());

    const node = document.createElement("script");
    node.type = "application/ld+json";
    node.dataset.jsonldId = key;
    node.textContent = serialized;
    document.head.appendChild(node);

    return () => {
      node.remove();
    };
  }, [id, serialized]);
}
